/**
 * !! What exactly is "enrichedFarms" data? !!
 *
 * The enrichedFarms data refers to the farms data that
 * has been made aware of and infused with other data (like fields).
 *
 * These hooks are meant for more general use than the basicFarms hooks.
 *
 * These hooks are to be used in places where farms data enriched
 * with its dependent data (like fields) is needed.
 *
 */

import { AxiosError } from "axios";

import { FarmsDataEnvelope } from "../../../models/farms";
import {
  QueryStatus,
  QueryEnvelope,
  EnsuredQueryEnvelope,
} from "../../../services/query";

import * as fieldsHooks from "../fields";

import * as basicFarmsHooks from "./basicFarms";
import { useSetFarmsGlobalActivityAndStatus } from "./farms.globalStatus";
import {
  computeCombinedStatus,
  computeCombinedError,
  computeCombinedData,
  computeEnvelopeOfEnsuredState,
  computeEnvelopeOfEnsuredStateWithData,
  useHandleStatusForGlobalStatus,
} from "./enrichedFarms.helpers";
import { farmsAccessError } from "./farms.helpers";

export { farmsAccessError };

export * from "./farms.invalidate";
export * from "./farms.mutations";

// ####################
// ## useFarmsStatus ##
// ####################

export type ResultOfUseFarmsStatus = QueryStatus;
export type UseFarmsStatus = () => ResultOfUseFarmsStatus;

/**
 * The returned value of this hook will contain the status
 * of the fetching of the farms data.
 */
export const useFarmsStatus: UseFarmsStatus = () => {
  const basicFarmsStatus = basicFarmsHooks.useBasicFarmsStatus();
  const fieldsStatus = fieldsHooks.useFieldsStatus();

  return computeCombinedStatus(basicFarmsStatus, fieldsStatus);
};

// ###################
// ## useFarmsError ##
// ###################

export type ResultOfUseFarmsError = AxiosError | null;
export type UseFarmsError = () => ResultOfUseFarmsError;

/**
 * The returned value of this hook will contain the error
 * that has occurred in the fetching of the farms data
 * or null if no error occurred.
 */
export const useFarmsError: UseFarmsError = () => {
  const basicFarmsError = basicFarmsHooks.useBasicFarmsError();
  const fieldsError = fieldsHooks.useFieldsError();

  return computeCombinedError(basicFarmsError, fieldsError);
};

// #######################
// ## useMaybeFarmsData ##
// #######################

export type ResultOfUseMaybeFarmsData = FarmsDataEnvelope | undefined;
export type UseMaybeFarmsData = () => ResultOfUseMaybeFarmsData;

/**
 * The returned value of this hook will contain
 * the farms data, which may be undefined.
 */
export const useMaybeFarmsData: UseMaybeFarmsData = () => {
  const basicFarmsData = basicFarmsHooks.useMaybeBasicFarmsData();
  const fieldsData = fieldsHooks.useMaybeFieldsData();

  return computeCombinedData(basicFarmsData?.farms, fieldsData?.fieldsByFarmId);
};

// ##################
// ## useFarmsData ##
// ##################

export type ResultOfUseFarmsData = FarmsDataEnvelope;
export type UseFarmsData = () => ResultOfUseFarmsData;

/**
 * The returned value of this hook will contain the farms data.
 *
 * This hook is guaranteed to contain farms data or will throw.
 */
export const useFarmsData: UseFarmsData = () => {
  const data = useMaybeFarmsData();
  if (data === undefined) {
    throw farmsAccessError;
  }
  return data;
};

// ###################
// ## useMaybeFarms ##
// ###################

export type ParamsOfUseMaybeFarms = { forceDisable?: boolean };
export type ResultOfUseMaybeFarms = QueryEnvelope<FarmsDataEnvelope>;
export type UseMaybeFarms = (
  params?: ParamsOfUseMaybeFarms,
) => ResultOfUseMaybeFarms;

/**
 * The `data` property returned from this hook will contain
 * the farms data, which may be undefined
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the farms data.
 */
export const useMaybeFarms: UseMaybeFarms = ({ forceDisable } = {}) => {
  const basicFarmsEnvelope = basicFarmsHooks.useMaybeBasicFarms({
    forceDisable,
  });
  const fieldsEnvelope = fieldsHooks.useMaybeFields({ forceDisable });

  const setFarmsGlobalActivityAndStatus = useSetFarmsGlobalActivityAndStatus();

  const status = computeCombinedStatus(
    basicFarmsEnvelope.status,
    fieldsEnvelope.status,
  );
  const error = computeCombinedError(
    basicFarmsEnvelope.error,
    fieldsEnvelope.error,
  );
  const data = computeCombinedData(
    basicFarmsEnvelope.data?.farms,
    fieldsEnvelope.data?.fieldsByFarmId,
  );

  useHandleStatusForGlobalStatus({ status, setFarmsGlobalActivityAndStatus });

  return computeEnvelopeOfEnsuredState(data, status, error);
};

// ##############
// ## useFarms ##
// ##############

export type ResultOfUseFarms = EnsuredQueryEnvelope<FarmsDataEnvelope>;
export type UseFarms = () => ResultOfUseFarms;

/**
 * The `data` property returned from this hook will contain
 * the farms data.
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the farms data.
 *
 * This hook is guaranteed to contain farms data or will throw.
 */
export const useFarms: UseFarms = () => {
  const { data, status, error } = useMaybeFarms();

  if (data === undefined) {
    throw farmsAccessError;
  }

  return computeEnvelopeOfEnsuredStateWithData(data, status, error);
};
